import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Collapse,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  Navbar,
  NavbarBrand,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faChevronDown,
  faEllipsisH,
  faFileInvoiceDollar,
  faFilePdf,
  faLaptop,
  faSignOutAlt,
  faTicketSimple,
  faUser,
  faUserCog,
  faUserSecret,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';
import queryString from 'query-string';

import AuthService from '../../Services/AuthService';
import RequestBuilder from '../../Lib/Requests';

import { RULES } from '../../Constants/AssetModel';
import {
  REMOTE_ACCESS_GUIDE_URL,
  REMOTE_ACCESS_WINDOWS_INSTALLER_URL,
} from '../../Constants/HelpURLs';
import { GEOFENCES, USER_PROFILES } from '../../Constants/InventoryResources';
import {
  BILLING,
  BILLING_WRITE,
  MAINTENANCE,
  MAINTENANCE_READ,
  REMOTE,
  REPORT,
  REPORT_READ,
} from '../../Constants/RBAC';
import { ASSET_INVENTORY, MAP } from '../../Constants/Routes';
import BrandingContext from '../../Context/BrandingContext';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import CurrentUserContext from '../../Context/CurrentUserContext';
import { ExitSubOrgContext } from '../../Context/ExitSubOrgContext';
import IconsContext from '../../Context/IconsContext';
import { ResetGlobalStateContext } from '../../Context/ResetGlobalStateContext';
import RootOrgContext from '../../Context/RootOrgContext';
import { Width } from '../../Context/WidthContext';
import { hasReadAccessToResource } from '../../RBAC/AccessTools';
import Can from '../../RBAC/Can';

import {
  AppsDropdown,
  MaintenanceDropdown,
  MobileFiltersCard,
  MobileFiltersToggler,
  MobileToggles,
  ReportsDropdown,
  TopLevelNav,
} from './HeaderDisplayComponents';

import MobileHeader from './MobileHeader';
import SearchBar from '../Search/SearchBar';

const { className: alertClass, styles: alertStyles } = css.resolve`
  .alert {
    top: 4.5rem;
    right: 12.5%;
    background-color: rgba(220, 53, 69, 0.8);
    position: fixed;
    z-index: 10000;
  }
  @media (min-width: 768px) {
    .alert {
      position: absolute;
    }
  }
`;

export default memo(Header);

const initState = {
  dropdownOpen: false,
  error: '',
  getFileErrorContent: '',
  helpOpen: false,
  isMobileFiltersOpen: false,
  showGetFileError: false,
};
let alertTimeout;

function Header(props) {
  const { handleSearchTerm, orgsMap, sideBar } = props;
  const [state, updateState] = useState(initState);

  const history = useHistory(),
    location = useLocation();
  const { pathname = '', search = '' } = location || {};

  const {
    documentation_url,
    isElevatUi,
    isSupraUi,
    isVersatileUi,
    isBarkoUi,
    logo_url,
    privacy_policy_url,
    youtube_url,
  } = useContext(BrandingContext);
  const { avatar } = useContext(IconsContext);
  const { _id: orgID, features, path } = useContext(CurrentOrgContext),
    exitSubOrg = useContext(ExitSubOrgContext),
    resetGlobalState = useContext(ResetGlobalStateContext),
    rootOrg = useContext(RootOrgContext),
    user = useContext(CurrentUserContext),
    width = useContext(Width);

  const handleBreadcrumbClick = useCallback(
    (nextOrgID, nextOrgName) => {
      if (nextOrgID === orgID) return;

      exitSubOrg(nextOrgID, nextOrgName);

      if (pathname !== ASSET_INVENTORY) history.push(ASSET_INVENTORY);
    },
    [exitSubOrg, history, orgID, pathname]
  );

  const api = useMemo(() => new RequestBuilder(), []),
    auth = useMemo(() => new AuthService(), []),
    pathArray = useMemo(
      () =>
        path
          ?.substring(path.indexOf(rootOrg?._id))
          ?.split('.')
          ?.map((id) => ({ id, name: orgsMap?.[id]?.name })) ?? [],
      [orgsMap, path, rootOrg?._id]
    ),
    usesV1Maintenance = useMemo(
      () => features?.maintenance_v1?.enabled ?? false,
      [features?.maintenance_v1?.enabled]
    );

  const { tab = '' } = queryString.parse(search);

  const mobileView = width < 768;
  const showFilters = useMemo(() => {
    switch (true) {
      case !mobileView:
        return false;
      case pathname.includes(GEOFENCES):
      case pathname.includes(USER_PROFILES):
        return false;
      case pathname.includes(RULES):
      default:
        return false;
    }
  }, [mobileView, pathname, tab]);

  const logout = async () => {
    try {
      await api?.request?.(auth?.buildLogoutRequest?.());
      auth?.logout?.();
      resetGlobalState?.();
      history.push('/');
    } catch (err) {
      const error = `Error logging out of the server: ${
        err.response?.data?.message ?? err.message
      }`;
      updateState((prevState) => ({ ...prevState, error }));
      auth?.logout?.();
      resetGlobalState?.();
      history.push('/');
    }
  };

  const toggleAlert = () => {
    updateState((prevState) => ({
      ...prevState,
      showGetFileError: !prevState.showGetFileError,
    }));
    alertTimeout = window.setTimeout(() => {
      updateState({ ...state, showGetFileError: false });
    }, 8000);
  };

  const toggleDropDown = () => {
    updateState((prevState) => ({
      ...prevState,
      dropdownOpen: !state.dropdownOpen,
    }));
  };

  const toggleHelp = () => {
    updateState((prevState) => ({
      ...prevState,
      helpOpen: !prevState.helpOpen,
    }));
  };

  const toggleMobileFilters = () => {
    updateState((prevState) => ({
      ...prevState,
      isMobileFiltersOpen: !prevState.isMobileFiltersOpen,
    }));
  };

  const _componentCleanup = useCallback(() => {
    window.clearTimeout(alertTimeout);
  }, []);

  useEffect(() => _componentCleanup, [_componentCleanup]);

  return (
    <MobileHeader logout={logout}>
      <Navbar className="h-75 pb-0 pl-lg-5" expand="lg">
        <NavbarBrand className="d-none d-lg-inline-block">
          {rootOrg?._id ? (
            <img
              alt="logo"
              className="img-fluid"
              src={rootOrg.logo || logo_url}
              style={{ maxHeight: '3rem', maxWidth: '6rem' }}
            />
          ) : null}
        </NavbarBrand>
        {showFilters ? (
          <MobileFiltersToggler toggleMobileFilters={toggleMobileFilters} />
        ) : null}
        <Collapse navbar>
          <Nav className="ml-3" navbar>
            <TopLevelNav path={MAP} isActive={pathname.startsWith(MAP)}>
              Map
            </TopLevelNav>
            <TopLevelNav
              isActive={pathname.startsWith('/organization')}
              path={ASSET_INVENTORY}
            >
              Organization
            </TopLevelNav>
            <TopLevelNav
              path="/events?tab=alerts"
              isActive={pathname.startsWith('/events')}
            >
              Events
            </TopLevelNav>
            {usesV1Maintenance ? (
              <Can
                no={() => null}
                orgID={orgID}
                perform={MAINTENANCE_READ}
                resource={MAINTENANCE}
                yes={() => (
                  <MaintenanceDropdown
                    partsIsActive={tab === 'parts'}
                    serviceIsActive={tab === 'service'}
                    scheduleIsActive={tab === 'schedule'}
                  />
                )}
              />
            ) : null}
            <Can
              no={() => null}
              orgID={orgID}
              perform={REPORT_READ}
              resource={REPORT}
              yes={() => <ReportsDropdown />}
            />
            <AppsDropdown
              engineanalysisIsActive={pathname.startsWith('/engineanalysis')}
              waterpumpIsActive={pathname.startsWith('/watertransfer')}
            />
          </Nav>
          <Nav
            horizontal="end"
            className="ml-auto align-items-center mt-1"
            navbar={true}
          >
            <SearchBar history={history} />
            <Dropdown
              nav
              inNavbar
              isOpen={state.dropdownOpen}
              toggle={toggleDropDown}
            >
              <DropdownToggle
                nav
                className="d-flex align-items-center mr-4"
                color="link"
              >
                <span className="text-nowrap font-weight-light mb-0 text-back-arrow-gray">
                  {user.firstName} {user.lastName}
                </span>
                {user?._id
                  ? (() => {
                      switch (true) {
                        case (isVersatileUi || isSupraUi) && !user.profileImage:
                          return (
                            <FontAwesomeIcon
                              className="text-back-arrow-gray mx-3"
                              icon={faUser}
                              size="lg"
                            />
                          );
                        default:
                          return (
                            <img
                              src={user.profileImage || avatar}
                              className="rounded-circle mx-3 align-middle"
                              alt="profile"
                              height="30"
                              width="30"
                            />
                          );
                      }
                    })()
                  : null}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  color={isVersatileUi ? 'var(--back-arrow-gray)' : ''}
                  className="mt-1"
                  size="lg"
                />
              </DropdownToggle>
              <DropdownMenu
                right
                style={{
                  boxShadow: '0px 0px 10px 0px var(--hr-gray)',
                  border: '0px',
                  zIndex: 1021,
                }}
              >
                <DropdownItem tag={Link} to="/settings" className="my-1">
                  <FontAwesomeIcon
                    className="dropdown-icon-style mr-2 text-third-gray"
                    fixedWidth
                    icon={faUserCog}
                    size="xs"
                  />
                  Settings
                </DropdownItem>
                <DropdownItem
                  className="my-1"
                  onClick={toggleHelp}
                  toggle={false}
                >
                  {state.helpOpen ? (
                    <FontAwesomeIcon
                      className="dropdown-icon-style mr-2 text-third-gray"
                      fixedWidth
                      icon={faAngleUp}
                      size="xs"
                    />
                  ) : (
                    <FontAwesomeIcon
                      className="dropdown-icon-style mr-2 text-third-gray"
                      fixedWidth
                      icon={faAngleDown}
                      size="xs"
                    />
                  )}
                  Help
                </DropdownItem>
                <Collapse isOpen={state.helpOpen}>
                  <DropdownItem
                    className="my-1 text-decoration-none text-text-dark"
                    href={youtube_url}
                    rel="noopener noreferrer"
                    tag="a"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      size="xs"
                      icon={faVideo}
                      className="dropdown-icon-style mr-2 text-third-gray ml-2"
                    />
                    Youtube
                  </DropdownItem>
                  <DropdownItem
                    className="my-1 text-decoration-none text-text-dark"
                    href={documentation_url}
                    rel="noopener noreferrer"
                    tag="a"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      size="xs"
                      icon={faFilePdf}
                      className="dropdown-icon-style mr-2 text-third-gray ml-2"
                    />
                    Documentation
                  </DropdownItem>
                  {hasReadAccessToResource(rootOrg?._id, REMOTE) ? (
                    <>
                      <DropdownItem
                        className="my-1 text-decoration-none text-text-dark"
                        href={REMOTE_ACCESS_GUIDE_URL}
                        rel="noopener noreferrer"
                        tag="a"
                        target="_blank"
                      >
                        <FontAwesomeIcon
                          size="xs"
                          icon={faFilePdf}
                          className="dropdown-icon-style mr-2 text-third-gray ml-2"
                        />
                        Remote Access Guide
                      </DropdownItem>
                      <DropdownItem
                        className="my-1 text-decoration-none text-text-dark"
                        href={REMOTE_ACCESS_WINDOWS_INSTALLER_URL}
                        rel="noopener noreferrer"
                        tag="a"
                        target="_blank"
                      >
                        <FontAwesomeIcon
                          size="xs"
                          icon={faLaptop}
                          className="dropdown-icon-style mr-2 text-third-gray ml-2"
                        />
                        Remote Access Windows Installer
                      </DropdownItem>
                    </>
                  ) : null}
                  {isVersatileUi || isBarkoUi ? (
                    <DropdownItem
                      className="my-1 text-decoration-none text-text-dark"
                      href={privacy_policy_url}
                      rel="noopener noreferrer"
                      tag="a"
                      target="_blank"
                    >
                      <FontAwesomeIcon
                        size="xs"
                        icon={faUserSecret}
                        className="dropdown-icon-style mr-2 text-third-gray ml-2"
                      />
                      Privacy Policy
                    </DropdownItem>
                  ) : null}
                </Collapse>
                {isElevatUi ? (
                  <DropdownItem
                    className="my-1 text-decoration-none"
                    href="https://elevat.atlassian.net/servicedesk/customer/portals"
                    rel="noopener noreferrer"
                    tag="a"
                    target="_blank"
                  >
                    <FontAwesomeIcon
                      className="dropdown-icon-style mr-2 text-third-gray"
                      fixedWidth
                      icon={faTicketSimple}
                      size="xs"
                    />
                    Support Portal
                  </DropdownItem>
                ) : null}
                <Can
                  no={() => null}
                  orgID={user?.rootOrgID}
                  perform={BILLING_WRITE}
                  resource={BILLING}
                  yes={() => (
                    <DropdownItem
                      className="my-1 text-decoration-none"
                      href={process.env['BILLING_PORTAL_URL']}
                      rel="noopener noreferrer"
                      tag="a"
                      target="_blank"
                    >
                      <FontAwesomeIcon
                        className="dropdown-icon-style mr-2 text-third-gray"
                        fixedWidth
                        icon={faFileInvoiceDollar}
                        size="xs"
                      />
                      Billing Portal
                    </DropdownItem>
                  )}
                />
                <DropdownItem onClick={logout} className="my-1">
                  <FontAwesomeIcon
                    className="dropdown-icon-style mr-2 text-third-gray"
                    fixedWidth
                    icon={faSignOutAlt}
                    size="xs"
                  />
                  Sign Out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </Nav>
        </Collapse>
      </Navbar>
      <Breadcrumb
        className="d-none d-lg-block h-25 pl-lg-5"
        listClassName="m-0 p-0 bg-white pl-lg-2"
      >
        {pathArray.length <= 4 ? (
          pathArray.map(({ id, name }) => (
            <BreadcrumbItem key={id}>
              <Button
                className="p-0 nine-tenth-text"
                color="link"
                onClick={() => handleBreadcrumbClick(id, name)}
              >
                {name}
              </Button>
            </BreadcrumbItem>
          ))
        ) : (
          <>
            <BreadcrumbItem>
              <Button
                className="p-0 nine-tenth-text"
                color="link"
                onClick={() =>
                  handleBreadcrumbClick(pathArray[0]?.id, pathArray[0]?.name)
                }
              >
                {pathArray[0]?.name}
              </Button>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <FontAwesomeIcon
                className="text-brand-primary"
                icon={faEllipsisH}
              />
            </BreadcrumbItem>
            {pathArray.slice(-3).map(({ id, name }) => (
              <BreadcrumbItem key={id}>
                <Button
                  className="p-0 nine-tenth-text"
                  color="link"
                  onClick={() => handleBreadcrumbClick(id, name)}
                >
                  {name}
                </Button>
              </BreadcrumbItem>
            ))}
          </>
        )}
      </Breadcrumb>
      {showFilters ? (
        <MobileFiltersCard
          handleSearchTerm={handleSearchTerm}
          open={state.isMobileFiltersOpen}
        >
          <MobileToggles {...sideBar.props.filters} />
        </MobileFiltersCard>
      ) : null}
      <Alert
        className={`${alertClass} w-75 text-center text-white border-0`}
        isOpen={state.showGetFileError}
        toggle={toggleAlert}
      >
        {state.getFileErrorContent}
      </Alert>
      {alertStyles}
      <style jsx>{`
        .dropdown-item:hover {
          background: var(--background-gray);
        }
        .dropdown-item:active {
          background: var(--third-gray) !important;
        }
      `}</style>
    </MobileHeader>
  );
}

Header.propTypes = {
  handleSearchTerm: PropTypes.func,
  orgsMap: PropTypes.object,
  sideBar: PropTypes.element,
};
